import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { apiClient } from '@/lib/apiClient';
// Type-only import in the other direction (treeLookup → HierarchyNodeDto), so this is not a runtime cycle.
import { compareTreeSiblings } from '@/lib/treeLookup';

// --- Types ---

export interface HierarchyNodeDto {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  parentId?: string | null;
  sortOrder: number;
  isActive: boolean;
  depth: number;
  materializedPath?: string | null;
  hasChildren: boolean;
}

export interface TreeSelectPickerProps {
  /** Fetch mode: load the tree from `${hierarchyApiPath}/tree`. Omit when `nodes` is supplied. */
  hierarchyApiPath?: string;
  /** Data mode: render this already-loaded node set instead of fetching (e.g. a client-built OrgUnit tree). */
  nodes?: HierarchyNodeDto[];
  value: string | null;
  onChange: (nodeId: string | null) => void;
  label?: string;
  showPath?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  size?: 'small' | 'medium';
  /** Open the dropdown as soon as the input receives focus (grid edit cells focus programmatically). */
  openOnFocus?: boolean;
  /** TextField variant — 'standard' matches underline-styled sibling controls (e.g. PrincipalPicker). */
  variant?: 'standard' | 'outlined' | 'filled';
  /**
   * Minimum dropdown width in px. By default the popup matches the input's width — useless
   * inside a narrow grid cell, where the tree gets truncated. Set (e.g. 360) to let the
   * popup grow past its anchor, same as the lookup edit cells do.
   */
  popupMinWidth?: number;
}

// --- Helpers ---

interface FlatOption {
  id: string;
  name: string;
  code: string;
  depth: number;
  path: string;
  hasChildren: boolean;
}

function buildFlatOptions(
  nodes: HierarchyNodeDto[],
  expandedIds: Set<string>,
): FlatOption[] {
  const map = new Map<string, HierarchyNodeDto>();
  const childrenMap = new Map<string, HierarchyNodeDto[]>();

  for (const node of nodes) {
    if (!node.isActive) continue;
    map.set(node.id, node);
    const parentKey = node.parentId ?? '__roots__';
    if (!childrenMap.has(parentKey)) childrenMap.set(parentKey, []);
    childrenMap.get(parentKey)!.push(node);
  }

  // Sort children by the shared sibling rule (sortOrder, then name)
  for (const children of childrenMap.values()) {
    children.sort(compareTreeSiblings);
  }

  function getPath(node: HierarchyNodeDto): string {
    const parts: string[] = [node.name];
    let cur = node;
    while (cur.parentId && map.has(cur.parentId)) {
      cur = map.get(cur.parentId)!;
      parts.unshift(cur.name);
    }
    return parts.join(' > ');
  }

  const result: FlatOption[] = [];
  function dfs(parentId: string | null) {
    const key = parentId ?? '__roots__';
    const children = childrenMap.get(key) || [];
    for (const node of children) {
      result.push({
        id: node.id,
        name: node.name,
        code: node.code,
        depth: node.depth,
        path: getPath(node),
        hasChildren: node.hasChildren,
      });
      // Only recurse into expanded nodes
      if (expandedIds.has(node.id)) {
        dfs(node.id);
      }
    }
  }
  dfs(null);
  return result;
}

// --- Component ---

export function TreeSelectPicker({
  hierarchyApiPath,
  nodes,
  value,
  onChange,
  label = 'Category',
  showPath = true,
  disabled = false,
  required = false,
  error,
  size = 'small',
  openOnFocus = false,
  variant,
  popupMinWidth,
}: TreeSelectPickerProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [allNodes, setAllNodes] = useState<HierarchyNodeDto[]>(nodes ?? []);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState<boolean>(!!nodes);

  // Data mode: when a node set is supplied (and may refresh async, e.g. a react-query list), use it
  // directly and never fetch.
  useEffect(() => {
    if (nodes) {
      setAllNodes(nodes);
      setLoaded(true);
    }
  }, [nodes]);

  // Expand ancestors of a node so it's visible in the tree
  const expandAncestorsOf = useCallback((nodeId: string | null, nodes: HierarchyNodeDto[]) => {
    if (!nodeId) return;
    const map = new Map<string, HierarchyNodeDto>();
    for (const n of nodes) map.set(n.id, n);

    const ancestors: string[] = [];
    let cur = map.get(nodeId);
    while (cur?.parentId) {
      ancestors.push(cur.parentId);
      cur = map.get(cur.parentId);
    }
    if (ancestors.length > 0) {
      setExpandedIds(prev => {
        const next = new Set(prev);
        for (const id of ancestors) next.add(id);
        return next;
      });
    }
  }, []);

  // Load the full tree on first open (Autocomplete controls open/close)
  const handleOpen = useCallback(() => {
    if (loaded) {
      // Tree already loaded — just ensure ancestors are expanded
      expandAncestorsOf(value, allNodes);
      return;
    }
    if (!hierarchyApiPath) return;
    setLoading(true);
    apiClient.get(`${hierarchyApiPath}/tree`)
      .then(res => {
        const nodes: HierarchyNodeDto[] = res.data;
        setAllNodes(nodes);
        setLoaded(true);
        expandAncestorsOf(value, nodes);
      })
      .finally(() => setLoading(false));
  }, [hierarchyApiPath, loaded, value, allNodes, expandAncestorsOf]);

  // Resolve display value for the currently selected node
  const [selectedOption, setSelectedOption] = useState<FlatOption | null>(null);
  useEffect(() => {
    if (!value) {
      setSelectedOption(null);
      return;
    }
    // Try from loaded nodes first
    const node = allNodes.find(n => n.id === value);
    if (node) {
      const map = new Map<string, HierarchyNodeDto>();
      for (const n of allNodes) map.set(n.id, n);
      const parts: string[] = [node.name];
      let cur = node;
      while (cur.parentId && map.has(cur.parentId)) {
        cur = map.get(cur.parentId)!;
        parts.unshift(cur.name);
      }
      setSelectedOption({ id: node.id, name: node.name, code: node.code, depth: node.depth, path: parts.join(' > '), hasChildren: node.hasChildren });
      return;
    }
    // Fetch single node if tree not loaded yet (fetch mode only; in data mode it's already in allNodes).
    if (!hierarchyApiPath) {
      setSelectedOption(null);
      return;
    }
    apiClient.get(`${hierarchyApiPath}/${value}`)
      .then(res => {
        const n: HierarchyNodeDto = res.data;
        setSelectedOption({ id: n.id, name: n.name, code: n.code, depth: n.depth, path: n.name, hasChildren: n.hasChildren });
      })
      .catch(() => setSelectedOption(null));
  }, [value, allNodes, hierarchyApiPath]);

  // Build visible options based on expanded state
  const options = useMemo(
    () => buildFlatOptions(allNodes, expandedIds),
    [allNodes, expandedIds],
  );

  const toggleExpand = useCallback((e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    e.preventDefault();
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  // Track the currently highlighted option for arrow key expand/collapse.
  //
  // Deliberately a ref, not state: MUI keeps its highlight as a positional index into the
  // option list and re-syncs it on any render where the list changed shape (see
  // `syncHighlightedIndex` in useAutocomplete). Re-rendering on every arrow key made that
  // sync run one keystroke after an expand — comparing the pre-expand list against the
  // post-expand one — so it either cleared the highlight (next arrow restarted at the root)
  // or silently re-pointed it at whichever node had inherited the old index, which is the
  // node Enter then committed. Reading the ref in the key handler is always current and
  // costs no render.
  const highlightedOptionRef = useRef<FlatOption | null>(null);

  // User-typed filter text. autoHighlight/autoSelect engage only while the user is actually
  // typing: with openOnFocus the popup is already open on a plain Tab-through, and a standing
  // first-option highlight would let blur commit a value the user never chose.
  const [typedInput, setTypedInput] = useState('');

  return (
    <Autocomplete
      value={selectedOption}
      onChange={(_, option) => {
        onChange(option?.id || null);
      }}
      onHighlightChange={(_, option) => {
        highlightedOptionRef.current = option;
      }}
      options={options}
      getOptionLabel={(option) => (showPath ? option.path : option.name)}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      disabled={disabled}
      size={size}
      autoHighlight={typedInput.length > 0}
      autoSelect={typedInput.length > 0}
      onInputChange={(_e, val, reason) => setTypedInput(reason === 'input' ? val : '')}
      openOnFocus={openOnFocus}
      onOpen={handleOpen}
      filterOptions={(opts, { inputValue }) => {
        if (!inputValue) return opts;
        // If pasted path like "Top node > Level A0 > Leaf", use only the leaf part
        const searchTerm = inputValue.includes(' > ')
          ? inputValue.split(' > ').pop()!.trim()
          : inputValue;
        const q = searchTerm.toLowerCase();
        if (q) {
          // When filtering, show ALL matching nodes regardless of expand state
          const allFlat = buildFlatOptions(allNodes, new Set(allNodes.map(n => n.id)));
          return allFlat.filter(o =>
            o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q)
          );
        }
        return opts;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={!!error}
          helperText={error}
          {...(variant ? { variant } : {})}
          onKeyDown={(e) => {
            const hl = highlightedOptionRef.current;
            // Commit the option the user can actually see highlighted. MUI resolves Enter
            // through its internal positional index, which a re-render landing between an
            // expand and Enter can silently re-point at a neighbouring node (it rewrites the
            // index without touching aria-activedescendant or the focus ring). Routing the
            // key through the highlighted option's own click handler keeps "what you see is
            // what you commit" and still runs MUI's full selection path (close, input reset).
            if (e.key === 'Enter') {
              const activeId = e.currentTarget
                .querySelector('input')
                ?.getAttribute('aria-activedescendant');
              const activeOption = activeId ? document.getElementById(activeId) : null;
              if (activeOption) {
                e.preventDefault();
                e.stopPropagation();
                activeOption.click();
                return;
              }
            }
            if (hl && e.key === 'ArrowRight' && hl.hasChildren && !expandedIds.has(hl.id)) {
              e.preventDefault();
              e.stopPropagation();
              setExpandedIds(prev => new Set(prev).add(hl.id));
            } else if (hl && e.key === 'ArrowLeft' && expandedIds.has(hl.id)) {
              e.preventDefault();
              e.stopPropagation();
              setExpandedIds(prev => { const next = new Set(prev); next.delete(hl.id); return next; });
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...restProps } = props as any;
        const isExpanded = expandedIds.has(option.id);

        return (
          <Box
            component="li"
            {...restProps}
            key={option.id}
            sx={{
              pl: `${option.depth * 20 + 8}px !important`,
              display: 'flex !important',
              alignItems: 'center !important',
              minHeight: '36px !important',
              py: '2px !important',
            }}
          >
            {/* Expand/collapse button */}
            {option.hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => toggleExpand(e, option.id)}
                onMouseDown={(e) => e.preventDefault()}
                sx={{ p: 0.25, mr: 0.5, flexShrink: 0 }}
              >
                {isExpanded ? <ExpandMore sx={{ fontSize: 18 }} /> : <ChevronRight sx={{ fontSize: 18 }} />}
              </IconButton>
            ) : (
              <Box sx={{ width: 26, mr: 0.5, flexShrink: 0 }} />
            )}
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" noWrap>{option.name}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{option.code}</Typography>
            </Box>
          </Box>
        );
      }}
      slotProps={{
        listbox: {
          sx: { maxHeight: 320 },
        },
        ...(popupMinWidth
          ? {
              popper: {
                placement: 'bottom-start' as const,
                style: { width: 'auto', minWidth: popupMinWidth },
              },
            }
          : {}),
      }}
    />
  );
}
